/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridApi } from '@ag-grid-community/core';
import omit from 'lodash-es/omit';

/**
 * Validates that a field's value falls within a specified range and updates error state
 * @param gridData Array of grid row data to validate
 * @param fieldName Name of the field to validate against the range
 * @param errorKey Error key to set in the row's errors object when validation fails
 * @param param3 Object containing min and max values for the valid range
 * @param gridAPI Grid API to apply transaction with updated error states
 * @returns Boolean indicating if validation passed (true) or failed (false)
 */
export function validateRangeField<T extends Record<string, any>>(
  gridData: T[],
  fieldName: keyof T,
  errorKey: string,
  { max, min }: { max: number; min: number },
  gridAPI: GridApi<T>
): boolean {
  const rowsToUpdate: T[] = [];
  let hasError = false;

  gridData.forEach((row) => {
    const value = row[fieldName];
    let fieldHasError = false;

    if (value < min || value > max) {
      hasError = true;
      fieldHasError = true;
    }

    rowsToUpdate.push({
      ...row,
      errors: fieldHasError
        ? { ...(row.errors || {}), [errorKey]: true }
        : omit(row.errors || {}, errorKey),
    });
  });

  if (rowsToUpdate.length) {
    gridAPI.applyTransaction({ update: rowsToUpdate });
  }

  return !hasError;
}

/**
 * Validates required fields in grid data and updates error state
 * @param gridData Array of grid row data to validate
 * @param fieldName Name of the field to validate
 * @param errorKey Error key to set in the row's errors object when validation fails
 * @param gridAPI Grid API to apply transaction with updated error states
 * @returns Boolean indicating if validation passed (true) or failed (false)
 */
export function validateRequiredField<T extends Record<string, any>>(
  gridData: T[],
  fieldName: keyof T,
  errorKey: string,
  gridAPI: GridApi<T>
): boolean {
  const rowsToUpdate: T[] = [];
  let hasError = false;

  gridData.forEach((row) => {
    const isMissing = !row[fieldName];

    if (isMissing) {
      hasError = true;
    }

    rowsToUpdate.push({
      ...row,
      errors: isMissing
        ? { ...(row.errors || {}), [errorKey]: true }
        : omit(row.errors || {}, errorKey),
    });
  });

  if (rowsToUpdate.length) {
    gridAPI.applyTransaction({ update: rowsToUpdate });
  }

  return !hasError;
}

/**
 * Validates uniqueness of a field across all grid rows and updates error state
 * @param gridData Array of grid row data to validate
 * @param fieldName Name of the field to validate for uniqueness
 * @param errorKey Error key to set in the row's errors object when duplicates are found
 * @param gridAPI Grid API to apply transaction with updated error states
 * @returns Boolean indicating if validation passed (true = all values unique) or failed (false = duplicates found)
 */
export function validateUniqueField<T extends Record<string, any>>(
  gridData: T[],
  fieldName: keyof T,
  errorKey: string,
  gridAPI: GridApi<T>
): boolean {
  let hasError = false;

  const values = gridData.map((item) => item[fieldName]).filter(Boolean);

  const valueCounts = values.reduce(
    (acc, value) => {
      const key = String(value);

      acc[key] = (acc[key] || 0) + 1;

      return acc;
    },
    {} as Record<string, number>
  );
  const updatedRows = gridData.map((row) => {
    const fieldValue = String(row[fieldName]);
    const isDuplicate = fieldValue && valueCounts[fieldValue] > 1;

    if (isDuplicate) {
      hasError = true;
    }

    return {
      ...row,
      errors: isDuplicate
        ? { ...(row.errors || {}), [errorKey]: true }
        : omit(row.errors || {}, errorKey),
    };
  });

  gridAPI.applyTransaction({ update: updatedRows });

  return !hasError;
}
